import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { LombardConfirmationData, LombardFacade } from '@zero/lombard/domain';
import { PdfUriPipe } from '@zero/postbox/feature-postbox/web';
import { PostTitlePipe } from '@zero/postbox/ui-pipes';
import { TransactionAuthorizationComponent } from '@zero/shared/ui-web-components';
import { ButtonComponent, ModalComponent } from '@zero/shared/ui-web-design-system';
import { CustomerStateFacade } from '@zero/shared/util-global-state';
import { AuthPayloadTicket, AuthorizationData, Ticket, TicketData, TransactionAuthorizationResult } from '@zero/shared/util-models';
import { filter, map } from 'rxjs';

@Component({
  templateUrl: './lombard-confirmation-modal.component.html',
  styleUrls: ['./lombard-confirmation-modal.component.scss'],
  imports: [
    TranslocoPipe,
    PdfUriPipe,
    PostTitlePipe,
    TransactionAuthorizationComponent,
    FormsModule,
    ButtonComponent,
    AsyncPipe,
    ModalComponent,
  ],
})
export class LombardConfirmationModalComponent implements OnInit, OnDestroy {
  public readonly modal = inject(NgbActiveModal);
  public readonly facade = inject(LombardFacade);
  readonly #destroyRef = inject(DestroyRef);
  readonly #customerStateFacade = inject(CustomerStateFacade);

  public hasCheckedConditions = false;
  public hasConfirmed = false;
  public authorizePayload$ = this.facade.confirmationData$.pipe(map((data) => this.#createTicket(data)));

  ngOnInit(): void {
    this.facade.loadConfirmationData();
  }

  ngOnDestroy(): void {
    this.facade.clearConfirmationData();
  }

  public handleTransactionAuthorization(result: TransactionAuthorizationResult): void {
    result.success && this.facade.loadLombard();
    this.modal.close();
  }

  public dismiss(): void {
    this.facade.dismissOffer();
    this.facade.hasDismissedOffer$
      .pipe(
        filter((hasDismissedOffer) => !!hasDismissedOffer),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.modal.close());
  }

  #createTicket = (confirmationData: LombardConfirmationData): AuthorizationData<AuthPayloadTicket> => {
    const ticket = new Ticket();
    ticket.customerId = this.#customerStateFacade.customerBaseData.customerId;
    ticket.type = 'CREDIT_CONFIRMATION';
    ticket.ticketData = new TicketData();

    ticket.ticketData.additionalProperties = {};
    ticket.ticketData.additionalProperties[TicketData.ATTR_LOMBARD_CONFIRMED_DOCS] = confirmationData.docIdsToConfirm;

    return new AuthorizationData<AuthPayloadTicket>(new AuthPayloadTicket(ticket));
  };
}
